import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  CommunicationHubEntityType,
  Form,
  List,
  message,
  Row,
  Select,
  Typography,
  Upload,
  UploadContent,
} from 'components';
import { CloseIcon, CloseIconLight, DoneIcon } from 'components/common/icons';
import { useAttachmentTypeQuery } from 'api';
import { MAX_FILE_SIZE } from 'constants/index';
import styles from 'pages/admin/leads/styles.module.scss';
import { convertEnumsToReadableStrings } from 'utils/helpers';

const { Text } = Typography;
const { Dragger } = Upload;

interface IAttachmentForm {
  entity: CommunicationHubEntityType;
  attachmentType?: string;
  setAttachmentType: (type: string | undefined) => void;
  fileList: File[];
  setFileList: (files: File[]) => void;
  attachments: { type: string; file: File }[];
  setAttachments: (attachments: { type: string; file: File }[]) => void;
}

export const AttachmentForm = React.memo<IAttachmentForm>((props) => {
  const { entity, attachmentType, setAttachmentType, fileList, setFileList, attachments, setAttachments } = props;
  const { data: attachmentTypes = [] } = useAttachmentTypeQuery({ entityName: entity });
  const [disabledActions, setDisabledActions] = useState(true);
  const [error, setError] = useState('');

  const handleAdd = () => {
    if (!attachmentType || fileList.length === 0) {
      message.warning('Please select type and upload a file.');
      return;
    }
    setAttachments([...attachments, { type: attachmentType, file: fileList[0] }]);
    setAttachmentType(undefined);
    setFileList([]);
  };

  const handleFileSizeValidation = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setFileList([]);
      setError('File exceeds the maximum size of 5 MB.');
      return Upload.LIST_IGNORE; // Prevent adding the file
    } else {
      setFileList([file]);
      setError('');
    }
    return false;
  };

  const handleDelete = (index: number) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };

  const handleCancel = () => {
    setAttachmentType(undefined);
    setFileList([]);
  };

  useEffect(() => {
    setDisabledActions(!attachmentType || !fileList?.length);
  }, [fileList, attachmentType]);

  return (
    <>
      {attachments.length > 0 && (
        <List
          dataSource={attachments}
          style={{ marginBottom: 24 }}
          renderItem={(item, index) => (
            <List.Item
              style={{ background: '#F5F7FA', height: 32, marginBottom: 5, paddingLeft: 12, borderRadius: 8 }}
              actions={[
                <Button
                  key={'add'}
                  size={'small'}
                  type={'link'}
                  icon={<CloseIconLight />}
                  onClick={() => handleDelete(index)}
                />,
              ]}
            >
              <Text ellipsis={{ tooltip: true }} style={{ maxWidth: 500 }}>
                {item.type && (
                  <span className={styles.option_tag} style={{ marginRight: '4px' }}>
                    {convertEnumsToReadableStrings(item.type)}{' '}
                  </span>
                )}
                {item.file.name}
              </Text>
            </List.Item>
          )}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item label={'Attachment Type'}>
            <Select
              size={'large'}
              value={attachmentType}
              options={attachmentTypes}
              onChange={setAttachmentType}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="file" label={'Attachment'} validateStatus={error ? 'error' : ''} help={error ? error : ''}>
            <Dragger
              accept=".eml,.mp3,.pdf,.png,.jpg,.jpeg"
              style={{ height: 40 }}
              beforeUpload={handleFileSizeValidation}
              fileList={fileList as any}
              onRemove={() => {
                setFileList([]);
              }}
              maxCount={1}
            >
              <UploadContent
                title={'Upload or Drag and Drop File'}
                message={'Max size: 5MB / Format: EML, MP3, PDF, PNG, JPG, JPEG'}
              />
            </Dragger>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Row gutter={4} justify={'end'}>
            <Col>
              <Button
                disabled={disabledActions}
                color="default"
                variant="filled"
                onClick={handleCancel}
                icon={<CloseIcon />}
              />
            </Col>
            <Col>
              <Button disabled={disabledActions} type={'primary'} icon={<DoneIcon />} onClick={handleAdd} />
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
});
